import { SearchServiceBase, SearchApi } from 'src/services/search.service';
import { Injectable, inject } from '@angular/core';
import {
  SpinnerV2Service,
  StorageV2Service,
  GridUtilsV2,
  AssetService,
} from '@schenck/rotec-components';
import { MachineHistorySettingsService } from './machine-history-settings.service';
import { MachineHistory } from './machine-history.model';
import { switchMap, map } from 'rxjs/operators';
import { combineLatest, of } from 'rxjs';
import {
  gridSchedulingMultiselectFilter,
  enhanceSortWithScheduling,
} from 'src/components/task-interval.pipe';
import { MachineTaskService } from 'src/services';

@Injectable()
export class MachineTaskSearchApi implements SearchApi<MachineHistory> {
  private readonly taskService = inject(MachineTaskService);
  private readonly assetService = inject(AssetService);

  reload$ = this.taskService.taskChange$;

  search$(date: { from: Date; to: Date } | null) {
    return this.taskService.search$(false, date).pipe(
      switchMap((results) => {
        if (results.length === 0) {
          // necessary to handle this because combineLatest won't emit anything if it gets passed an empty array
          return of([]);
        }

        const searchResults$ = results.map((result) => {
          const machine$ = this.assetService.machine$(result.machineId);
          const plant$ = this.assetService.plantOfMachine$(machine$);

          return combineLatest([machine$, plant$]).pipe(
            map(([machine, plant]) => {
              const task: MachineHistory = {
                ...result,
                plantName: (plant && plant.name) || '',
                machineSerial: (machine && machine.serial) || '',
                machineName: machine && machine.name,
              };
              return task;
            })
          );
        });

        return combineLatest(searchResults$);
      })
    );
  }
}

@Injectable()
export class MachineHistorySearchService extends SearchServiceBase<MachineHistory> {
  constructor() {
    const searchApi = inject(MachineTaskSearchApi);
    const loadingService = inject(SpinnerV2Service);
    const settingsService = inject(MachineHistorySettingsService);
    const storageService = inject(StorageV2Service);

    super(searchApi, loadingService, settingsService, storageService, {
      key: 'MachineHistorySearch',
      primaryDateProp: 'executionDate',
      filter: {
        plantName: GridUtilsV2.multiselectFilter,
        machineName: GridUtilsV2.multiselectFilter,
        machineSerial: GridUtilsV2.multiselectFilter,
        schenckId: GridUtilsV2.textFilter,
        customerId: GridUtilsV2.textFilter,
        title: GridUtilsV2.textFilter,
        priority: GridUtilsV2.checkboxFilter,
        responsibleCompany: GridUtilsV2.radioFilter,
        responsible: GridUtilsV2.multiselectFilter,
        executive: GridUtilsV2.multiselectFilter,
        dueDate: GridUtilsV2.dateRangeFilter,
        description: GridUtilsV2.textFilter,
        component: GridUtilsV2.multiselectFilter,
        type: GridUtilsV2.multiselectFilter,
        requiredTooling: GridUtilsV2.textFilter,
        possibleSpareParts: GridUtilsV2.textFilter,
        scheduling: gridSchedulingMultiselectFilter,
        executionDate: GridUtilsV2.dateRangeFilter,
      },
      searchResultComparator: enhanceSortWithScheduling(GridUtilsV2.defaultResultComparator),
    });
  }
}
